import { Box, Flex, Text } from '@chakra-ui/react';
import { useRef } from 'react';

import { useResizeObserver } from '@revelio/core';
import {
  DIVERGING_BAR_HEADER_LABEL_HEIGHT,
  DivergingBarChart,
  FormatType,
} from '@revelio/replots';

import { usePlotMetric } from '../../hooks/usePlotMetric';
import type {
  BreakdownSingleDataSet,
  SingleDataSet,
} from '../DataProvider/types';
import { DataType } from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { useActiveColors } from './GroupedBar/useActiveColors';
import { getPrettyFormatValue } from './utils/format';
import { getFormat } from './utils/getFormat';
import { getDivergingBarConfig } from './utils/map-to-diverging-data';
import { transformComparisonData } from './utils/transform-comparison-data';

const FOOTER_HEIGHT = 25;

function sortByLabelOrder<T extends { label: string }>(
  data: T[],
  labelOrder: string[]
): T[] {
  const orderMap = new Map(labelOrder.map((l, i) => [l, i]));
  return [...data].sort(
    (a, b) =>
      (orderMap.get(a.label) ?? Infinity) - (orderMap.get(b.label) ?? Infinity)
  );
}

function isSingleDataSet(data: unknown): data is SingleDataSet {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as SingleDataSet).type === DataType.SINGLE &&
    'data' in data &&
    Array.isArray((data as SingleDataSet).data)
  );
}

export const DivergingBarCombo = ({
  dataSet,
}: {
  dataSet: BreakdownSingleDataSet;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();
  const { colorMap } = useActiveColors();
  const format = (() => {
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    return getFormat({ metric });
  })();

  const differenceFormat =
    format === FormatType.PERCENTAGE
      ? FormatType.PERCENTAGE_POINT
      : FormatType.DECIMAL;

  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const baseline = item?.plot?.comparisonTransformation?.baseline ?? 'second';

  const containerRef = useRef<HTMLDivElement>(null);
  useResizeObserver(containerRef);

  const tornadoConfig = getDivergingBarConfig(
    dataSet,
    item,
    colorMap,
    getPrettyFormatValue(format),
    { baseline, dualViewColors: true }
  );

  const transformedDifference = transformComparisonData(dataSet, {
    baseline,
  });
  if (!isSingleDataSet(transformedDifference)) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Text fontSize="sm" color="gray.600">
          Incompatible data for comparison
        </Text>
      </Box>
    );
  }

  const differenceDataSet = transformedDifference;
  const differenceFormatValue = getPrettyFormatValue(differenceFormat);
  const differenceConfig = getDivergingBarConfig(
    differenceDataSet,
    item,
    colorMap,
    differenceFormatValue
  );

  const sortedLabels = [...tornadoConfig.divergingBarData]
    .sort((a, b) => (a.leftValue ?? 0) - (b.leftValue ?? 0))
    .map((d) => d.label);

  const tornadoDataSorted = sortByLabelOrder(
    tornadoConfig.divergingBarData,
    sortedLabels
  );
  const differenceDataSorted = sortByLabelOrder(
    differenceConfig.divergingBarData,
    sortedLabels
  );

  const combo = item?.plot?.divergingBarCombo;
  const comparisonLabel = combo?.comparisonLabel?.trim() ?? '';
  const differenceLabel = combo?.differenceLabel?.trim() ?? '';

  const renderCenteredFooter = (label: string) => () => (
    <Box
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text fontSize="sm" fontWeight={600}>
        {label}
      </Text>
    </Box>
  );

  const showEitherFooter = comparisonLabel !== '' || differenceLabel !== '';
  const showLeftFooter = comparisonLabel !== '';
  const showRightFooter = differenceLabel !== '';

  return (
    <Flex
      ref={containerRef}
      width="100%"
      height="100%"
      flexDirection="row"
      gap={4}
      position="relative"
      overflow="hidden"
    >
      <Flex flex={1} minWidth={0} flexDirection="column" position="relative">
        <Box flex="1 1 auto" minHeight={0}>
          <DivergingBarChart
            data={tornadoDataSorted}
            format={format}
            leftColor={tornadoConfig.leftColor}
            rightColor={tornadoConfig.rightColor}
            tooltipContent={tornadoConfig.tooltipContent}
            showBarLabels={showBarLabels}
            valueLabel={tornadoConfig.valueLabel}
            sortData={false}
            leftHeaderLabel={
              combo?.leftHeaderLabel?.trim() || tornadoConfig.leftFooterLabel
            }
            rightHeaderLabel={
              combo?.rightHeaderLabel?.trim() || tornadoConfig.rightFooterLabel
            }
            footer={
              showLeftFooter ? renderCenteredFooter(comparisonLabel) : undefined
            }
            footerHeight={showEitherFooter ? FOOTER_HEIGHT : undefined}
          />
        </Box>
      </Flex>
      <Flex flex={1} minWidth={0} flexDirection="column" position="relative">
        <Box flex="1 1 auto" minHeight={0}>
          <DivergingBarChart
            data={differenceDataSorted}
            format={differenceFormat}
            leftColor={differenceConfig.leftColor}
            rightColor={differenceConfig.rightColor}
            tooltipContent={differenceConfig.tooltipContent}
            showBarLabels={showBarLabels}
            valueLabel={differenceConfig.valueLabel}
            sortData={false}
            topOffset={DIVERGING_BAR_HEADER_LABEL_HEIGHT}
            showYAxisLabels={false}
            footer={
              showRightFooter
                ? renderCenteredFooter(differenceLabel)
                : undefined
            }
            footerHeight={showEitherFooter ? FOOTER_HEIGHT : undefined}
          />
        </Box>
      </Flex>
    </Flex>
  );
};
